import { AdminApiError, type AdminClient, type SicboJackpotOverride } from '@apx/admin-sdk';
import { sicboJackpotSchema } from '@apx/integrator-config/schemas';
import { askPrompt } from '../modal.js';
import { fmtDateTime } from '../format.js';

export interface SicboJackpotPageDeps {
  client: AdminClient;
  integratorId: string;
  gameId: string;
  roundEngineBaseUrl: string;
}

// Mirrors DEFAULT_JACKPOT_CONFIG + poolContributionRate in @apx/game-sicbo.
const DEFAULT_VALUE: SicboJackpotOverride = {
  minDrainBalanceCredits: '100000',
  poolContributionRate: 0.05,
};

/**
 * Sic Bo jackpot editor — Option A (triple-drain). Unlike greedyking's
 * tier-threshold model, the drain is deterministic from the dice: any triple
 * pays the largest qualifying triple bet, provided the pool sits at or above
 * `minDrainBalanceCredits`. `poolContributionRate` is the share of stake that
 * feeds the pool each round. Two-admin approval applies.
 */
export async function renderSicboJackpotPage(
  root: HTMLElement,
  deps: SicboJackpotPageDeps,
): Promise<void> {
  root.innerHTML = `
    <h1 class="page-title">Jackpot Config — Sic Bo</h1>
    <p class="page-sub">Triple-drain jackpot: every triple fires a payout to the largest qualifying triple bet, as long as the pool is at or above the minimum drain balance. No tier thresholds, no per-round roll — the drain is deterministic from the dice.</p>

    <div class="card">
      <h2>Configuration</h2>
      <div class="form-grid">
        <label>Minimum drain balance (credits)
          <input id="sj-min-drain" type="number" min="0" step="10000" />
          <span class="muted">Below this the pool keeps growing; no drain fires.</span>
        </label>
        <label>Pool contribution rate (0..1)
          <input id="sj-rate" type="number" min="0" max="1" step="0.005" />
          <span class="muted">Share of each stake that feeds the pool.</span>
        </label>
      </div>
      <div class="row">
        <button id="sj-save" class="primary">Propose change</button>
        <button id="sj-reset" class="ghost">Reset to defaults</button>
        <span id="sj-msg" class="muted"></span>
      </div>
    </div>

    <div class="card">
      <h2>Pending approvals for this surface</h2>
      <div id="sj-pending"><p class="muted">Loading…</p></div>
    </div>
  `;

  const minDrainEl = root.querySelector<HTMLInputElement>('#sj-min-drain')!;
  const rateEl = root.querySelector<HTMLInputElement>('#sj-rate')!;
  const msgEl = root.querySelector<HTMLElement>('#sj-msg')!;

  function fillForm(v: SicboJackpotOverride): void {
    minDrainEl.value = v.minDrainBalanceCredits;
    rateEl.value = String(v.poolContributionRate);
  }
  function readForm(): SicboJackpotOverride {
    return {
      minDrainBalanceCredits: minDrainEl.value || '0',
      poolContributionRate: Number(rateEl.value),
    };
  }

  const { value: current } = await deps.client.getSurface(deps.integratorId, deps.gameId, 'jackpot');
  fillForm((current as SicboJackpotOverride | null) ?? DEFAULT_VALUE);

  root.querySelector<HTMLButtonElement>('#sj-reset')!.addEventListener('click', () => {
    fillForm(DEFAULT_VALUE);
    msgEl.textContent = 'Reset to defaults (not saved).';
    msgEl.className = 'muted';
  });

  root.querySelector<HTMLButtonElement>('#sj-save')!.addEventListener('click', async () => {
    msgEl.textContent = '';
    const parsed = sicboJackpotSchema.safeParse(readForm());
    if (!parsed.success) {
      msgEl.textContent = parsed.error.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join(' • ');
      msgEl.className = 'error';
      return;
    }
    try {
      const res = await deps.client.proposeSurface(deps.integratorId, deps.gameId, 'jackpot', parsed.data);
      msgEl.textContent = `Pending approval ${res.approval.id}. A different admin must approve.`;
      msgEl.className = 'muted';
      await refreshPending();
    } catch (err) {
      msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
      msgEl.className = 'error';
    }
  });

  async function refreshPending(): Promise<void> {
    const all = await deps.client.listApprovals(deps.integratorId, 'pending');
    const subset = all.filter((a) => a.surface === 'jackpot' && a.gameId === deps.gameId);
    const container = root.querySelector<HTMLElement>('#sj-pending')!;
    if (subset.length === 0) {
      container.innerHTML = '<p class="muted">No pending changes.</p>';
      return;
    }
    container.innerHTML = `<table>
      <thead><tr><th>Created</th><th>Proposer</th><th></th></tr></thead>
      <tbody>${subset
        .map(
          (a) => `<tr>
            <td>${fmtDateTime(a.createdAt)}</td>
            <td><code>${a.proposerUserId.slice(0, 8)}…</code></td>
            <td>
              <button data-approve="${a.id}">Approve</button>
              <button data-reject="${a.id}" class="ghost">Reject</button>
            </td>
          </tr>`,
        )
        .join('')}</tbody>
    </table>`;
    container.querySelectorAll<HTMLButtonElement>('button[data-approve]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        try {
          await deps.client.approve(btn.dataset.approve!);
          msgEl.textContent = 'Approved — jackpot config now live.';
          msgEl.className = 'muted';
          await refreshPending();
          const fresh = await deps.client.getSurface(deps.integratorId, deps.gameId, 'jackpot');
          fillForm((fresh.value as SicboJackpotOverride | null) ?? DEFAULT_VALUE);
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
    container.querySelectorAll<HTMLButtonElement>('button[data-reject]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const reason = await askPrompt('Rejection reason?');
        if (!reason) return;
        try {
          await deps.client.reject(btn.dataset.reject!, reason);
          await refreshPending();
        } catch (err) {
          msgEl.textContent = err instanceof AdminApiError ? `${err.code}: ${err.message}` : String(err);
          msgEl.className = 'error';
        }
      });
    });
  }
  await refreshPending();
}
